export type PolicyTravelOption = {
  label: string
  description: string
  price: string
}

export type PolicyTravelSection = {
  description: string
  options: PolicyTravelOption[]
  note: string
}

export type PolicySection = {
  title: string
  paragraphs?: string[]
  items?: string[]
}

export type RetainerPolicyItem = {
  label: string
  value: string
  reference?: string
}

export const BOOKING_POLICY_PARAGRAPHS = [
  'A signed photography agreement and booking retainer are required to reserve coverage. Dates are not reserved until both the agreement and retainer have been received.',
  'Booking retainers are applied toward the total coverage price and are non-refundable if the client cancels the booking.',
  'Sports schedules change. If a competition is rescheduled or canceled by the school, team, league, venue, or governing organization, the retainer can generally be transferred to another available date.',
  'If Jessica Deen Photography is unable to photograph a confirmed booking, any applicable payment will be refunded.',
  'Full cancellation, rescheduling, and payment terms are provided in the photography agreement before booking.',
]

export const RETAINER_POLICY: RetainerPolicyItem[] = [
  {
    label: 'STANDARD ATHLETE / SHARED ATHLETE / SINGLE-EVENT TEAM COVERAGE',
    value: '30% booking retainer, with a $100 minimum',
  },
  {
    label: 'ATHLETE SEASON PACKAGES',
    value: '25% booking retainer',
  },
  {
    label: 'FULL TEAM SEASON',
    value: '20% booking retainer',
    reference: '$900 at current package pricing',
  },
  {
    label: 'JV + VARSITY PROGRAM SEASON',
    value: '20% booking retainer',
    reference: '$1,500 at current package pricing',
  },
]

export const TRAVEL_POLICY_FALLBACK: PolicyTravelSection = {
  description: 'Jessica is based in the Tri-Valley / Pleasanton area. Local travel is included with standard coverage, and events outside the local service area may include an additional travel fee based primarily on expected drive time from that area.',
  options: [
    { label: 'LOCAL TRAVEL', description: 'Up to approximately 45 minutes each way', price: 'Included' },
    { label: 'REGIONAL TRAVEL', description: 'Approximately 45-90 minutes each way', price: '+$100' },
    { label: 'EXTENDED REGIONAL TRAVEL', description: 'Approximately 90 minutes-2 hours each way', price: '+$175' },
    { label: 'LONG-DISTANCE TRAVEL', description: 'More than approximately 2 hours each way', price: 'Custom quote / minimum coverage commitment may apply' },
  ],
  note: "Travel fees are assessed per competition date unless otherwise included in a custom quote. Substantial parking fees, tolls, lodging, airfare, or other required travel expenses are quoted separately. Travel time is based on normal expected driving conditions from the Tri-Valley / Pleasanton area to the venue, rather than unusual day-of traffic incidents.",
}

export const DIGITAL_PURCHASE_POLICY: PolicySection = {
  title: 'DIGITAL PHOTO PURCHASES',
  paragraphs: [
    'High-resolution digital photo purchases include a downloadable image file for personal use, including personal printing, family sharing, and personal social media use. Commercial, sponsor, merchandise, resale, or publication use requires separate permission.',
    'Digital purchases are delivered on the receipt page and by email. Download links are available for a limited time, so save your files promptly and contact Jessica if a link expires or the email does not arrive.',
    'Because digital files are delivered immediately, purchases are generally non-refundable. If you accidentally purchase the wrong image or have trouble accessing your file, contact Jessica with the order reference and photo details so it can be reviewed.',
  ],
}

export const POLICY_PAGE_SECTIONS: PolicySection[] = [
  {
    title: 'BOOKING + RETAINERS',
    paragraphs: [
      'A signed photography agreement and booking retainer are required before coverage dates are reserved. Retainers reserve photographer availability and are credited toward the total booking price.',
    ],
    items: [
      'Standard athlete, shared athlete, and single-event team coverage: 30% booking retainer, with a $100 minimum.',
      'Athlete season packages: 25% booking retainer.',
      'Full Team Season and JV + Varsity Program Season packages: 20% booking retainer.',
    ],
  },
  {
    title: 'CANCELLATIONS + RESCHEDULING',
    paragraphs: [
      'Booking retainers are non-refundable when the client voluntarily cancels a booking. If a sports schedule changes, the retainer can generally be transferred to another available date.',
      'If Jessica Deen Photography cannot photograph a confirmed booking, payments applicable to services not provided will be refunded.',
    ],
  },
  {
    title: 'SPORTS SCHEDULE CHANGES',
    paragraphs: [
      'Weather delays, bracket changes, venue changes, and school or league schedule changes are common in sports. If an event is rescheduled, the first step is to check photographer availability for the new date.',
      'If the rescheduled date cannot be accommodated, the retainer or payment may be applied to another mutually available competition or refunded when appropriate under the final photography agreement.',
    ],
  },
  {
    title: 'PAYMENT',
    paragraphs: [
      'Remaining balances are handled according to the photography agreement for the booking. Season-package balances may be divided into scheduled payments. Final payment terms are provided in the season photography agreement.',
    ],
  },
  {
    title: 'IMAGE DELIVERY',
    paragraphs: [
      'Standard galleries are typically delivered within 48-72 hours of the covered competition. Larger tournaments, multi-session events, travel projects, or custom commercial deliverables may require additional editing time.',
    ],
  },
  {
    title: 'USAGE + LICENSING',
    paragraphs: [
      'Personal athlete coverage includes personal printing, family sharing, and personal social media use. Full-team coverage includes organic use by the purchasing team, school, club, or booster organization on websites, social media, newsletters, and similar non-paid communications.',
      'Sponsor advertising, paid campaigns, merchandise, resale, commercial publications, third-party marketing, and other commercial uses may require separate licensing.',
    ],
  },
]
